import { useEffect, useRef } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setQueuedPrompt } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import {
  getActiveLubavSession,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';

import { findLastUserIndex, findReportRequestIndex } from '../utils/reportRequest';

const STRINGS = appStrings.modules.satActBooster.results.lubav;

/**
 * Queues the plan summary while this run's report request is still the newest user turn — the chat
 * survives a Replan, so anything after it means the summary already went. Waits on `sessionId`: a
 * queued prompt is consumed even when the send fails.
 */
export const useBoosterPlanSummary = (enabled: boolean) => {
  const dispatch = useAppDispatch();
  const activeSession = useAppSelector(getActiveLubavSession);
  const messages = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.officeHoursMessages,
  );
  const sessionId = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.sessionId,
  );
  const isTyping = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.loading,
  );
  const isStreaming = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.streaming,
  );
  const hasQueuedPrompt = useAppSelector((state) => !!state.sessionState.queuedPrompt);
  const requestedRef = useRef(false);

  useEffect(() => {
    if (!enabled || requestedRef.current || hasQueuedPrompt) return;
    if (!sessionId || isTyping || isStreaming) return;

    const reportIndex = findReportRequestIndex(messages);

    // -1 means the transcript hasn't been recalled yet, not that nothing has been asked.
    if (reportIndex === -1 || reportIndex !== findLastUserIndex(messages)) return;

    requestedRef.current = true;

    dispatch(
      setQueuedPrompt({
        label: STRINGS.summarizeLabel,
        chatMessage: STRINGS.summarizeChatMessage,
        prompt: STRINGS.summarizePrompt,
        timestamp: Date.now(),
      }),
    );
  }, [enabled, hasQueuedPrompt, sessionId, isTyping, isStreaming, messages, dispatch]);
};
